import React, { useState, useEffect } from 'react';
import { 
    getNotificationPermission, 
    requestFCMToken, 
    getExistingFCMToken, 
    isSafari,
    isNotificationSupported,
    saveTokenToBackend 
} from '../../cms/utils/firebaseUtils';

const NotificationPermission = ({ onTokenReceived }) => {
    const [permissionStatus, setPermissionStatus] = useState('default');
    const [fcmToken, setFcmToken] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);

    useEffect(() => {
        checkInitialPermission();
    }, []);

    const checkInitialPermission = async () => {
        if (!isNotificationSupported()) {
            setPermissionStatus('unsupported');
            return;
        }

        const permission = getNotificationPermission();
        setPermissionStatus(permission);

        if (permission === 'granted') {
            // try to catch token if granted
            const token = await getExistingFCMToken();
            if (token) {
                setFcmToken(token);
                onTokenReceived && onTokenReceived(token);
                console.log("FCM Token", token);
                
                // save token to backend
                try {
                    await saveTokenToBackend(token);
                } catch (error) {
                    console.error('Failed to save token to backend:', error);
                }
            }
        } else if (permission === 'default') {
            // for safari, we use NotificationBar component, so here is not displayed
            // for other browsers, we can automatically request permission
            if (!isSafari()) {
                handleRequestPermission();
            }
        }
    };

    const handleRequestPermission = async () => {
        setIsLoading(true);
        try {
            const token = await requestFCMToken();
            setFcmToken(token);
            setPermissionStatus('granted');
            setShowPermissionPrompt(false);
            onTokenReceived && onTokenReceived(token);
            console.log("FCM Token", token);
            
            // save token to backend
            try {
                await saveTokenToBackend(token);
            } catch (error) {
                console.error('Failed to save token to backend:', error);
            }
        } catch (error) {
            console.error("Error requesting FCM token", error);
            setPermissionStatus('denied');
            setShowPermissionPrompt(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDismiss = () => {
        setShowPermissionPrompt(false);
    };

    // if not supported, do not display any content
    if (permissionStatus === 'unsupported') {
        return null;
    }

    // if already has permission and has token, do not display any content
    if (permissionStatus === 'granted' && fcmToken) {
        return null;
    }

    // if not need to display permission prompt, do not display any content
    if (!showPermissionPrompt) {
        return null;
    }

    return (
        <div style={styles.container}>
            <div style={styles.notification}>
                <div style={styles.content}>
                    <h4 style={styles.title}>Enable Notification</h4>
                    <p style={styles.message}>
                        {isSafari() 
                            ? 'Click the button below to enable push notifications and receive important updates.' 
                            : 'Enable push notifications to receive important updates.'}
                    </p>
                    <div style={styles.buttons}>
                        <button 
                            onClick={handleRequestPermission}
                            disabled={isLoading}
                            style={styles.allowButton}
                        >
                            {isLoading ? 'Requesting...' : 'Allow Notification'}
                        </button>
                        <button 
                            onClick={handleDismiss}
                            style={styles.dismissButton}
                        >
                            Later
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: '20px'
    },
    notification: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        maxWidth: '400px',
        margin: '0 20px',
        overflow: 'hidden'
    },
    content: {
        padding: '20px'
    },
    title: {
        margin: '0 0 10px 0',
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#333'
    },
    message: {
        margin: '0 0 20px 0',
        fontSize: '14px',
        color: '#666',
        lineHeight: '1.5'
    },
    buttons: {
        display: 'flex',
        gap: '10px',
        justifyContent: 'flex-end'
    },
    allowButton: {
        backgroundColor: '#007bff',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        padding: '8px 16px',
        fontSize: '14px',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    },
    dismissButton: {
        backgroundColor: '#6c757d',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        padding: '8px 16px',
        fontSize: '14px',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    }
};

export default NotificationPermission; 